import { useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Button, List, ListItem, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Plot from "react-plotly.js";
import "../styles/results.css";
import { usePatient } from '../context/patientContext';

const ABOVE_90_DBHL = 91;
const AUDIOGRAM_X_AXIS_VALUES = ['250', '500', '1000', '2000', '4000', '6000', '8000'];

export default function Results() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { finalizeAndEnqueue } = usePatient();
  const mode = location.state?.mode ?? (location.state?.from === '/screening' ? 'screening' : 'diagnostic');
  const data = JSON.parse(sessionStorage.getItem(mode === 'screening' ? 'screeningResults' : 'diagnosticResults') || '{}');
  const answers = JSON.parse(sessionStorage.getItem('questionnaireAnswers') || '{}');

  const degreeFromDb = useCallback((level) => {
    if (typeof level !== 'number' || Number.isNaN(level)) {
      return 'Unknown';
    }
    if (level <= 15) return t('results.degreesofhearingloss.normal');
    if (level <= 25) return t('results.degreesofhearingloss.slight');
    if (level <= 40) return t('results.degreesofhearingloss.mild');
    if (level <= 55) return t('results.degreesofhearingloss.moderate');
    if (level <= 70) return t('results.degreesofhearingloss.moderatelySevere');
    if (level <= 90) return t('results.degreesofhearingloss.severe');
    return t('results.degreesofhearingloss.profound');
  }, [t]);

  const hearingLossDegrees = useMemo(() => {
    if (mode === 'screening' || !data) return null;

    const freqs = [500, 1000, 2000, 4000, 6000, 8000];
    const severityRank = ['Normal', 'Slight', 'Mild', 'Moderate', 'Moderately severe', 'Severe', 'Profound'];

    let leftWorstValue = -Infinity;
    let leftWorstFreq = null;
    freqs.forEach((f) => {
      const val = data?.Left?.[f];
      if (typeof val === 'number' && !Number.isNaN(val) && val > leftWorstValue) {
        leftWorstValue = val;
        leftWorstFreq = f;
      }
    });

    let rightWorstValue = -Infinity;
    let rightWorstFreq = null;
    freqs.forEach((f) => {
      const val = data?.Right?.[f];
      if (typeof val === 'number' && !Number.isNaN(val) && val > rightWorstValue) {
        rightWorstValue = val;
        rightWorstFreq = f;
      }
    });

    if (leftWorstValue > 90) leftWorstValue = 90;
    if (rightWorstValue > 90) rightWorstValue = 90;

    const leftCategory = leftWorstValue !== -Infinity ? degreeFromDb(leftWorstValue) : 'Not measured';
    const rightCategory = rightWorstValue !== -Infinity ? degreeFromDb(rightWorstValue) : 'Not measured';

    const leftRank = leftCategory === 'Not measured' ? -1 : severityRank.indexOf(leftCategory);
    const rightRank = rightCategory === 'Not measured' ? -1 : severityRank.indexOf(rightCategory);
    const overallCategory = (leftRank >= rightRank ? leftCategory : rightCategory) || 'Unknown';
    const overallFreq = leftRank >= rightRank ? leftWorstFreq : rightWorstFreq;
    const overallValue = leftRank >= rightRank ? leftWorstValue : rightWorstValue;

    return {
      leftWorstValue,
      leftWorstFreq,
      leftCategory,
      rightWorstValue,
      rightWorstFreq,
      rightCategory,
      overallCategory,
      overallFreq,
      overallValue,
    };
  }, [mode, data, degreeFromDb]);

  const { patientBullets, referrals } = (() => {
    const left = data?.Left || {};
    const right = data?.Right || {};
    let freqs, leftEar, rightEar;
    let bothPassed = false;
    let failedEar;
    const a = answers || {};
    const out = [];
    const referrals = {};
    function addReferral(type, reason) {
      if (!referrals[type]) {
        referrals[type] = [];
      }

      if (reason && !referrals[type].includes(reason)) {
        referrals[type].push(reason);
      }
    }
    if (mode === 'screening') {
      freqs = [1000, 2000, 4000, 6000];
      leftEar = freqs.map(f => left[f] ? 1 : 0);
      rightEar = freqs.map(f => right[f] ? 1 : 0);

      const leftPassed = leftEar.every(v => v === 1);
      const rightPassed = rightEar.every(v => v === 1);

      if (leftPassed && rightPassed) {
        out.push(t('results.bullets.screeningPass'));
        bothPassed = true;
      } else if (!leftPassed && !rightPassed) {
        out.push(t('results.bullets.screeningFailBoth'));
        addReferral("Audiologist", "Both ears failed screening test.")
      } else if (!leftPassed && rightPassed) {
        out.push(t('results.bullets.screeningFailLeft'));
        addReferral("Audiologist", "Left ear failed screening test.")
      } else if (leftPassed && !rightPassed) {
        out.push(t('results.bullets.screeningFailRight'));
        addReferral("Audiologist", "Right ear failed screening test.")
      }
    }
    else {
      freqs = [500, 1000, 2000, 4000, 6000, 8000];
      leftEar = freqs.map(f => left[f]);
      rightEar = freqs.map(f => right[f]);

      const leftPassed = leftEar.every(threshold => threshold <= 15);
      const rightPassed = rightEar.every(threshold => threshold <= 15);
      bothPassed = leftPassed && rightPassed;
      if (!leftPassed && !rightPassed) {
        failedEar = 'both ears';
      } else if (!leftPassed) {
        failedEar = 'left ear';
      } else if (!rightPassed) {
        failedEar = 'right ear';
      } else {
        failedEar = 'neither ear';
      }

      let shouldShowMessage = false;

      for (let i = 0; i < freqs.length - 1; i++) {
        if (Math.abs(leftEar[i] - leftEar[i + 1]) >= 20 || Math.abs(rightEar[i] - rightEar[i + 1]) >= 20) {
          shouldShowMessage = true;
          break;
        }
      }

      if (!shouldShowMessage) {
        const highFreqIndices = [2, 3, 4, 5];
        for (let i = 0; i < highFreqIndices.length; i++) {
          for (let j = i + 1; j < highFreqIndices.length; j++) {
            const idx1 = highFreqIndices[i];
            const idx2 = highFreqIndices[j];
            if (Math.abs(leftEar[idx1] - leftEar[idx2]) >= 15 || Math.abs(rightEar[idx1] - rightEar[idx2]) >= 15) {
              shouldShowMessage = true;
              break;
            }
          }
          if (shouldShowMessage) break;
        }
      }
      if (!shouldShowMessage && (a.q1 === 'left' || a.q1 === 'right')) {
        shouldShowMessage = true;
      }

      if (shouldShowMessage) {
        out.push(t('results.bullets.singleEarEnt'));
        addReferral("ENT", "Patient reported sudden hearing loss in one or both ears"); // TODO: This shouldn't say that the patient reported sudden hearing loss anymore
      }
    }
    // Q1 - Have you ever had your hearing tested?
    // Q1a - When did you last have your hearing tested?
    // Q1b - Why have you not had your hearing tested?
    // Q2 - Do you think you might have hearing loss, or have you previously been diagnosed with hearing loss?
    if (bothPassed === true) {
      if (a.q2 === 'no') {
        out.push(t('results.bullets.normalAsExpected'));
      } else {
        out.push(t('results.bullets.normalButConcerned'));
      }
    } else {
      if (a.q2 === 'no') {
        out.push(t('results.bullets.lossUnexpected'));
        addReferral("Audiologist", "Diagnostic test showed hearing loss even though patient reported no hearing loss.")
      } else if (a.q2a === 'both' || a.q2b === 'both') {
        if (failedEar === 'both ears') {
          out.push(t('results.bullets.lossBothBothExpected'));
        } else if (failedEar === 'left ear') {
          out.push(t('results.bullets.lossLeftBothExpected'));
        } else if (failedEar === 'right ear') {
          out.push(t('results.bullets.lossRightBothExpected'));
        }
        addReferral("Audiologist", `Diagnostic test showed hearing loss in ${failedEar} when questionnaire indicated hearing loss in both ears.`)
      } else if (a.q2a === 'left' || a.q2b === 'left') {
        if (failedEar === 'both ears') {
          out.push(t('results.bullets.lossBothLeftExpected'));
        } else if (failedEar === 'left ear') {
          out.push(t('results.bullets.lossLeftLeftExpected'));
        } else if (failedEar === 'right ear') {
          out.push(t('results.bullets.lossRightLeftExpected'));
        }
        addReferral("Audiologist", `Diagnostic test showed hearing loss in ${failedEar} when questionnaire indicated hearing loss in the left ear.`)
      } else if (a.q2a === 'right' || a.q2b === 'right') {
        if (failedEar === 'both ears') {
          out.push(t('results.bullets.lossBothRightExpected'));
        } else if (failedEar === 'left ear') {
          out.push(t('results.bullets.lossLeftRightExpected'));
        } else if (failedEar === 'right ear') {
          out.push(t('results.bullets.lossRightRightExpected'));
        }
        addReferral("Audiologist", `Diagnostic test showed hearing loss in ${failedEar} when questionnaire indicated hearing loss in the right ear.`)

      } else {
        out.push(t('results.bullets.lossGeneral'));
        addReferral("Audiologist", "Diagnostic test showed hearing loss.")
      }
    }

    // Q3 - Noise Exposure
    if (a.q3 === 'yes') {
      if (bothPassed === true) {
        out.push(t('results.bullets.noiseExposurePrevention'));
      }
      else {
        out.push(t('results.bullets.noiseExposureWithLoss'));
        addReferral("Audiologist", "Patient showed hearing loss consistent with noise exposure.")
      }
    }

    // Q4 — fullness, pain, drainage
    if (a.q4 === 'yes') {
      out.push(t('results.bullets.earSymptomsEnt'));
      addReferral("ENT", "Patient reported having ear pain, ear fullness, or ear drainage.")
    }

    // Q5 - Ear Surgery
    if (a.q5 === 'yes-patient') {
      out.push(t('results.bullets.priorSurgeryPatient'));
      addReferral("ENT", "Patient reported having previously had an ear surgery.")
    }
    else if (a.q5 === 'yes-family') {
      out.push(t('results.bullets.priorSurgeryFamily'))
      addReferral("ENT", "Patient reported that a family member has had ear surgery.")
    }

    // Q6- Tinnitus
    if (a.q6 === 'yes') {
      if (bothPassed === true) {
        out.push(t('results.bullets.tinnitusGeneral'));
      }
      else {
        out.push(t('results.bullets.tinnitusWithLoss'));
      }
    }
    const q6aLConstant = Array.isArray(a.q6a) && a.q6a.includes('left-constant');
    const q6aRConstant = Array.isArray(a.q6a) && a.q6a.includes('right-constant');
    if ((q6aLConstant && !q6aRConstant) || (q6aRConstant && !q6aLConstant)) {
      out.push(t('results.bullets.oneSidedTinnitusEnt'))
      addReferral("ENT", "Patient reports having constant tinnitus in one ear.")
    }

    // Q6c yes OR either q6b rating 4 or 5 → pulsatile/very bothersome tinnitus
    const leftBother = a.q6bl ? parseInt(a.q6bl, 10) : null;
    const rightBother = a.q6br ? parseInt(a.q6br, 10) : null;

    const anyBother45 = [leftBother, rightBother].some(v => Number.isInteger(v) && v >= 4);
    if (anyBother45) {
      out.push(t('results.bullets.tinnitusMentalHealth'));
      addReferral("Psychologist", "Patient reported having very bothersome tinnitus in one or both ears.")
    }
    if (a.q6c === 'yes') {
      out.push(t('results.bullets.pulsatileTinnitusEnt'));
      addReferral("ENT", "Patient reported having tinnitus that sounds like a heartbeat in one or both ears.")
    }
    // Any q6b rating 2–5 → tinnitus management recommendations
    const anyBother2345 = [leftBother, rightBother].some(v => Number.isInteger(v) && v >= 2);
    if (anyBother2345) {
      out.push(t('results.bullets.tinnitusManagement'));
      addReferral("TinnitusRecs", "Patient reported bothersome tinnitus (severity rating between 2 - 5 on questionnaire)")
    }
    // Q7 - Medical diagnoses

    // Q8 - Dizziness / balance problems / falls
    if (a.q8a?.includes("Spinning")) {
      addReferral("ENT", "Patient reported having 'spinning' like dizziness.")
    }
    // Q8a - Type of dizziness/balance symptom

    return {
      patientBullets: out,
      referrals
    };

  })();

  const HearingNumber = useMemo(() => {
    if (mode === 'screening' || !data) return null;
    const freqs = [500, 1000, 2000, 4000];
    const leftValues = freqs
      .map((f) => {
        const val = data?.Left?.[f];
        return val === ABOVE_90_DBHL ? 90 : val;
      })
      .filter((val) => typeof val === 'number' && !Number.isNaN(val));
    const rightValues = freqs
    .map((f) => {
      const val = data?.Right?.[f];
      return val === ABOVE_90_DBHL ? 90 : val;
    })
    .filter((val) => typeof val === 'number' && !Number.isNaN(val));

    const values = [...leftValues, ...rightValues];
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  }, [mode, data]);

  const getHearingNumberClass = (value) => {
    if (value <= 15) return 'results-hearing-value--normal';
    if (value <= 25) return 'results-hearing-value--slight';
    if (value <= 40) return 'results-hearing-value--mild';
    if (value <= 55) return 'results-hearing-value--moderate';
    if (value <= 70) return 'results-hearing-value--moderately-severe';
    return 'results-hearing-value--severe';
  };

  sessionStorage.setItem(mode === 'screening' ? 'screeningReferrals' : 'diagnosticReferrals', JSON.stringify(referrals));

  function HearingChart() {
    const left = data?.Left || {};
    const right = data?.Right || {};
    let freqs, leftY, rightY, layout;
    let leftSymbols, rightSymbols;

    const isMissedAtMax = (threshold) => {
      return threshold === ABOVE_90_DBHL;
    };

    const mapFreqsToLinearDisplay = (freqs) => {
      return freqs.map(freq => AUDIOGRAM_X_AXIS_VALUES.indexOf(String(freq)));
    };

    if (mode === 'screening') {
      freqs = [1000, 2000, 4000, 6000];
      leftY = freqs.map(f => left[f] ? 1 : 0);
      rightY = freqs.map(f => right[f] ? 1 : 0);

      leftSymbols = freqs.map(() => "x");
      rightSymbols = freqs.map(() => "circle-open");

      layout = {
        title: { text: t('results.chart.title', { dBHL: location.state.dBHLTested }) },
        xaxis: {
          title: { text: t('results.chart.frequency') },
          range: [-0.2, 7.2],
          tickvals: mapFreqsToLinearDisplay(AUDIOGRAM_X_AXIS_VALUES),
          ticktext: AUDIOGRAM_X_AXIS_VALUES
        },
        yaxis: {
          title: { text: t('results.chart.result') },
          range: [-0.2, 1.2],
          tickvals: [0, 1],
          ticktext: [`&#10060; ${t('results.chart.fail')}`, `&#9989; ${t('results.chart.pass')}`],
        },
        height: 380,
        legend: { orientation: "h", y: -0.3 },
      };

    } else {
      freqs = [500, 1000, 2000, 4000, 6000, 8000];
      leftY = freqs.map(f => left[f]);
      rightY = freqs.map(f => right[f]);

      leftSymbols = freqs.map(f =>
        isMissedAtMax(left[f]) ? "square-open" : "x"
      );

      rightSymbols = freqs.map(f =>
        isMissedAtMax(right[f]) ? "triangle-up-open" : "circle-open"
      );

      leftY = freqs.map(f => left[f] === ABOVE_90_DBHL ? 90 : left[f]);
      rightY = freqs.map(f => right[f] === ABOVE_90_DBHL ? 90 : right[f]);

      layout = {
        xaxis: {
          title: { text: t('results.chart.frequency') },
          range: [-0.2, 7.2],
          tickvals: mapFreqsToLinearDisplay(AUDIOGRAM_X_AXIS_VALUES),
          ticktext: AUDIOGRAM_X_AXIS_VALUES
        },
        yaxis: {
          title: { text: t('results.chart.thresholdDb') },
          range: [120, -20],
          tickvals: [-20, -10, 0, 20, 40, 60, 80, 90, 110],
        },
        height: 380,
        showlegend: { orientation: "h", y: -0.2 },
      };
    }

    const leftTrace = {
      x: mapFreqsToLinearDisplay(freqs),
      y: leftY,
      name: t('results.chart.leftEar'),
      type: "scatter",
      mode: "lines+markers",
      marker: {
        color: "blue",
        size: 9,
        symbol: leftSymbols
      },
      line: { color: "blue" }
    };

    const rightTrace = {
      x: mapFreqsToLinearDisplay(freqs),
      y: rightY,
      name: t('results.chart.rightEar'),
      type: "scatter",
      mode: "lines+markers",
      marker: {
        color: "red",
        size: 9,
        symbol: rightSymbols
      },
      line: { color: "red" }
    };


    const config = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: [
        'zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d',
        'resetScale2d',
        'hoverClosestCartesian', 'hoverCompareCartesian', 'toggleSpikelines'
      ],
    };

    return <Plot data={[leftTrace, rightTrace]} layout={layout} config={config} className="results-plot" />;
  }

  useEffect(() => {
    if (!('hasUploaded' in sessionStorage)) {
      const a = answers || {};
      const left = data?.Left || {};
      const right = data?.Right || {};

      const payload = {};

      let localDate = new Date();
      localDate = new Date(
        localDate.getTime() - localDate.getTimezoneOffset() * 60 * 1000
      );

      const map_questionnaire = {
        q1: { yes: 1, no: 2, unsure: 3 },
        q1a: {
          'Within-the-past-year': 1,
          '2-5-years': 2,
          'within-the-past-5-10-years': 3,
          'more-than-10-years': 4,
        },
        q1b: {
          'no-concerns': 1,
          'inconvenient': 2,
          'to-expensive': 3,
          'no-info': 4,
          'distrust': 5,
        },
        q2: { yes: 1, no: 0 },
        q2a: { right: 1, left: 2, both: 3 },
        q2b: { right: 1, left: 2, both: 3 },
        q3: { yes: 1, no: 0 },
        q3a: { impulse: 1, intermittent: 2, continuous: 3 },
        q3b: {
          'few-instances': 1,
          'more-than-few': 2,
          'continuous-months': 3,
          'continuous-years': 4,
        },
        q4: { yes: 1, no: 0 },
        q5: { 'yes-patient': 1, 'yes-family': 2, no: 3 },
        q6: { yes: 1, no: 0 },
        q6a: {
          'left-constant': 1,
          'left-intermittent': 2,
          'right-constant': 3,
          'right-intermittent': 4,
        },
        q6bl: {
          '1': 1,
          '2': 2,
          '3': 3,
          '4': 4,
          '5': 5,
        },
        q6br: {
          '1': 1,
          '2': 2,
          '3': 3,
          '4': 4,
          '5': 5,
        },
        q6c: { yes: 1, no: 0 },
        q7: {
          'hbp': 1,
          'Diabetes': 2,
          'CHD': 3,
          'Stroke': 4,
          'D/A': 5,
          'Depression': 6,
          'None': 7,
        },
        q8: { yes: 1, no: 0 },
        q8a: {
          'Spinning': 1,
          'Floating': 2,
          'Lightheaded': 3,
          'Disorientation': 4,
          'Imbalance': 5,
        },
      };

      // Questionnaire responses
      if (a.q1) payload.had_hearing_tested = map_questionnaire.q1[a.q1];
      if (a.q1a) payload.when_last_test = map_questionnaire.q1a[a.q1a];
      if (a.q1b) {
        for (const choice of a.q1b) {
          payload[`why_no_test___${map_questionnaire.q1b[choice]}`] = 1;
        }
      }
      if (a.q2) payload.think_diagnosed_hearing_loss = map_questionnaire.q2[a.q2];
      if (a.q2a) payload.think_hearing_loss_in = map_questionnaire.q2a[a.q2a];
      if (a.q2b) payload.diagnosed_hearing_loss_in = map_questionnaire.q2b[a.q2b];

      if (a.q3) payload.exposed_to_loud_noises = map_questionnaire.q3[a.q3];
      if (a.q3a) payload.type_of_loud_noise = map_questionnaire.q3a[a.q3a];
      if (a.q3b) payload.duration_of_noise_exposure = map_questionnaire.q3b[a.q3b];
      if (a.q4) payload.has_ear_pain = map_questionnaire.q4[a.q4];
      if (a.q5) {
        for (const choice of a.q5) {
          payload[`had_ear_surgery___${map_questionnaire.q5[choice]}`] = 1;
        }
      }
      if (a.q5) {
        for (const choice of a.q5) {
          payload[`had_ear_surgery___${map_questionnaire.q5[choice]}`] = 1;
        }
      }
      if (a.q6) payload.has_had_tinnitus = map_questionnaire.q6[a.q6];
      if (a.q6a) {
        for (const choice of a.q6a) {
          payload[`which_ear_tinnitus___${map_questionnaire.q6a[choice]}`] = 1;
        }
      }
      if (a.q6bl) payload.bothersome_tinnitus_left = map_questionnaire.q6bl[a.q6bl];
      if (a.q6br) payload.bothersome_tinnitus_right = map_questionnaire.q6br[a.q6br];
      if (a.q6c) payload.tinnitus_heartbeat = map_questionnaire.q6c[a.q6c];
      if (a.q7) {
        for (const choice of a.q7) {
          payload[`any_diagnoses___${map_questionnaire.q7[choice]}`] = 1;
        }
      }
      if (a.q8) payload.balance_issues = map_questionnaire.q8[a.q8];
      if (a.q8a) {
        for (const choice of a.q8a) {
          payload[`balance_symptoms___${map_questionnaire.q8a[choice]}`] = 1;
        }
      }
      const right_ear_passed = Object.values(right).every((item) => item === true) ? 1 : 0;
      const left_ear_passed = Object.values(left).every((item) => item === true) ? 1 : 0;
      if (mode === 'screening') {
        // Screening results
        payload.hz_500_passed_right = right['500'] ? 1 : 0;
        payload.hz_500_passed_left = left['500'] ? 1 : 0;
        payload.hz_1000_passed_right = right['1000'] ? 1 : 0;
        payload.hz_1000_passed_left = left['1000'] ? 1 : 0;
        payload.hz_2000_passed_right = right['2000'] ? 1 : 0;
        payload.hz_2000_passed_left = left['2000'] ? 1 : 0;
        payload.hz_4000_passed_right = right['4000'] ? 1 : 0;
        payload.hz_4000_passed_left = left['4000'] ? 1 : 0;
        payload.hz_6000_passed_right = right['6000'] ? 1 : 0;
        payload.hz_6000_passed_left = left['6000'] ? 1 : 0;
        payload.right_ear_passed = right_ear_passed;
        payload.left_ear_passed = left_ear_passed;
      } else {
        // Diagnostic Test results
        payload.right_threshold_500 = right['500']?.toString() ?? '';
        payload.left_threshold_500 = left['500']?.toString() ?? '';
        payload.right_threshold_1000 = right['1000']?.toString() ?? '';
        payload.left_threshold_1000 = left['1000']?.toString() ?? '';
        payload.right_threshold_2000 = right['2000']?.toString() ?? '';
        payload.left_threshold_2000 = left['2000']?.toString() ?? '';
        payload.right_threshold_4000 = right['4000']?.toString() ?? '';
        payload.left_threshold_4000 = left['4000']?.toString() ?? '';
        payload.right_threshold_6000 = right['6000']?.toString() ?? '';
        payload.left_threshold_6000 = left['6000']?.toString() ?? '';
        payload.right_threshold_8000 = right['8000']?.toString() ?? '';
        payload.left_threshold_8000 = left['8000']?.toString() ?? '';
      }

      // referrals
      const map_referral_type = {
        "ENT": 1,
        "Audiologist": 2,
        "TinnitusRecs": 3,
        "Psychologist": 4,
        "HearingDevices": 5,
      }

      const referralTypes = Object.keys(referrals);

      payload.received_referral = 0;
      if (referralTypes.length !== 0) {
        payload.received_referral = 1;
        for (const type of referralTypes) {
          payload[`referral_type___${map_referral_type[type]}`] = 1;
        }
        for (const type of referralTypes) {
          const singleString = referrals[type].join("|");
          payload[`reasons_${type.toLowerCase()}`] = singleString;
        }
      }

      let testType = '';
      if (mode === 'screening') {
        testType = 'Screening Test'
      } else {
        testType = 'Diagnostic Test'
      }
      let testPatch = {
        testDate: localDate.toISOString().split('T')[0],
        testType: testType,
        results: payload,
      }

      finalizeAndEnqueue(testPatch);
      sessionStorage.setItem('hasUploaded', 'true');
    }
    // None of these dependencies can update while we are in this file, and even if they do update
    //   the code in this useEffect will not run because it will fail the if condition on the first
    //   line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className="results-page">
      <Box className="results-topbar">
        {JSON.stringify(referrals) !== '{}' ? <Button
          variant="contained"
          data-cy="results_next-steps_btn"
          color="primary"
          onClick={() => navigate('/nextsteps', { state: { ...(location.state || {}), from: location.pathname } })}
          aria-label={t('results.nextSteps')}
        >
          {t('results.nextSteps')}
        </Button> : <Button
          variant="contained"
          data-cy="results_another-test_btn"
          onClick={() => navigate('/testFlow', { state: { ...(location.state || {}), from: location.pathname } })}
          aria-label={t('results.takeAnotherTest')}
        >
          {t('results.takeAnotherTest')}
        </Button>}
      </Box>

      <Box className="results-layout">
        <Box elevation={3} className="results-left-column">
          {/* Results Summary */}
          <Paper elevation={3} className="results-summary-card" >
            <Typography variant="h6" className="results-summary-title">{t('results.title')}</Typography>

            {patientBullets.length > 0 ? (
              <List dense className="results-list">
                {patientBullets.map((line, idx) => (
                  <ListItem key={idx} className="results-list-item">
                    <ListItemText primary={line} slotProps={{primary: {className: 'results-list-text',},}}/>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography className="results-empty-text">
                {t('results.empty')}
              </Typography>
            )}
          </Paper>

          {HearingNumber !== null && (
            <Paper elevation={3} className="results-hearing-card">
              <Box className="results-hearing-main">
                <Typography variant="h6" className="results-hearing-title">{t('results.hearingnumber.label')} </Typography>
                <Typography
                  variant="h4"
                  className={`results-hearing-value ${getHearingNumberClass(HearingNumber)}`}
                  data-cy="hearing_number"
                >
                  {HearingNumber.toFixed(0)}
                </Typography>
                <Typography
                  component="a"
                  href="https://hearingnumber.org/what-is-the-hearing-number/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  className="results-hearing-link">{t('results.hearingnumber.learnmore')}
                </Typography>
              </Box>
              <Box className="results-hearing-legend">
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--normal" />
                  <Typography variant="body2">
                    {t('results.hearingnumber.normal')}
                  </Typography>
                </Box>
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--slight"/>
                  <Typography variant="body2">
                    {t('results.hearingnumber.slight')}
                  </Typography>
                </Box>
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--mild"  />
                  <Typography variant="body2">
                    {t('results.hearingnumber.mild')}
                  </Typography>
                </Box>
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--moderate" />
                  <Typography variant="body2" >
                    {t('results.hearingnumber.moderate')}
                  </Typography>
                </Box>
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--moderately-severe" />
                  <Typography variant="body2">
                    {t('results.hearingnumber.moderatelySevere')}
                  </Typography>
                </Box>
                <Box className="results-hearing-legend-item">
                  <Box className="results-hearing-dot results-hearing-dot--severe" />
                  <Typography variant="body2" >
                    {t('results.hearingnumber.severe')}
                  </Typography>
                </Box>
              </Box>

            </Paper>
          )}
        </Box>

        {/* Audiogram */}
        <Paper elevation={3} className="results-chart-card">
          <HearingChart />

          <Box className="results-chart__legend">
            <Box className="results-chart__item">
              <Box className="results-chart__icon results-chart__icon--blue">✕</Box>
              <Typography variant="body2">{t('results.chart.leftEar')}</Typography>
            </Box>

            <Box className="results-chart__item">
              <Box className="results-chart__icon results-chart__icon--red">○</Box>
              <Typography variant="body2">{t('results.chart.rightEar')}</Typography>
            </Box>

            {mode !== 'screening' && (
              <>
                <Box className="results-chart__item">
                  <Box className="results-chart__icon results-chart__icon--blue">□</Box>
                  <Typography variant="body2">{t('results.chart.leftMissed')}</Typography>
                </Box>

                <Box className="results-chart__item">
                  <Box className="results-chart__icon results-chart__icon--red">△</Box>
                  <Typography variant="body2">{t('results.chart.rightMissed')}</Typography>
                </Box>
              </>
            )}

            {mode !== 'screening' && hearingLossDegrees && (
              <Box className="results-degree-box">
                <Typography variant="h6" className="results-degree-title">
                  {t('results.degreesofhearingloss.label')}</Typography>
                <Typography variant="body2" data-cy="degree_loss_left" className="results-degree-text">{t('results.degreesofhearingloss.left')}: {hearingLossDegrees.leftWorstFreq != null ? `${hearingLossDegrees.leftWorstValue.toFixed(1)} dB HL at ${hearingLossDegrees.leftWorstFreq} Hz (${hearingLossDegrees.leftCategory})` : 'Not measured'}</Typography>
                <Typography variant="body2" data-cy="degree_loss_right" className="results-degree-text">{t('results.degreesofhearingloss.right')}: {hearingLossDegrees.rightWorstFreq != null ? `${hearingLossDegrees.rightWorstValue.toFixed(1)} dB HL at ${hearingLossDegrees.rightWorstFreq} Hz (${hearingLossDegrees.rightCategory})` : 'Not measured'}</Typography>
                <Typography variant="body2" data-cy="degree_loss_overall" className="results-degree-text results-degree-text--overall">{t('results.degreesofhearingloss.overall')}: {hearingLossDegrees.overallFreq != null ? `${hearingLossDegrees.overallValue.toFixed(1)} dB HL at ${hearingLossDegrees.overallFreq} Hz (${hearingLossDegrees.overallCategory})` : 'Not measured'}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}